import { EntityModel } from '@app/data-layer/models/entities';
import {
  LclIsfEsLinkModel,
  LCLShipmentModel,
  QuoteManualModel,
  QuoteModel,
  QuoteUpdateModel,
  SurveyInfoModel,
} from '@data-layer/models/lcl';
import { HttpErrorResponse } from '@angular/common/http';
import { DocumentModel } from '@data-layer/models/documents';
import { UntypedFormGroup } from '@angular/forms';

export class OffsetChange {
  public static readonly type = '[LCL Shipping] Offset Change';

  constructor(public payload: number) {}
}

export class ResetLCLData {
  public static readonly type = '[LCL Shipping] Reset LCL Data';
}

export class FetchLCLQuotes {
  public static readonly type = '[LCL Shipping] Fetch LCL Quotes';

  constructor(public payload: { companyId: number; offset?: number; limit?: number }) {}
}

export class FetchLCLManualQuotes {
  public static readonly type = '[LCL Shipping] Fetch LCL Manual Quotes';

  constructor(public payload: { companyId: number; offset?: number; limit?: number }) {}
}

export class FetchLCLShipments {
  public static readonly type = '[LCL Shipping] Fetch LCL Shipments';

  constructor(public payload: { companyId: number; offset?: number; limit?: number }) {}
}

export class SetDefaultPaginationState {
  public static readonly type = '[LCL Shipping] Set Default Pagination State';
}

export class FetchQuote {
  public static readonly type = '[LCL Shipping] Fetch Quote';

  constructor(public payload: { quoteId: number; isLoading?: boolean }) {}
}

export class FetchQuoteSuccess {
  public static readonly type = '[LCL Shipping] Fetch Quote Success';
}

export class FetchQuoteError {
  public static readonly type = '[LCL Shipping] Fetch Quote Error';

  constructor(public payload: HttpErrorResponse) {}
}

export class FetchShipment {
  public static readonly type = '[LCL Shipping] Fetch Shipment';

  constructor(public payload: number) {}
}

export class FetchShipmentSuccess {
  public static readonly type = '[LCL Shipping] Fetch Shipment Success';
}

export class FetchShipmentError {
  public static readonly type = '[LCL Shipping] Fetch Shipment Error';

  constructor(public payload: HttpErrorResponse) {}
}

export class UpdateLCLEntity {
  public static readonly type = '[LCL Shipping] Update LCL Entity';

  constructor(public payload: { entityId: number; entity: Partial<EntityModel> }) {}
}

export class OrderQuote {
  public static readonly type = '[LCL Shipping] Order Quote';

  constructor(public payload: { companyId: number; quote: QuoteModel; shipper: EntityModel; consignee: EntityModel }) {}
}

export class OrderQuoteSuccess {
  public static readonly type = '[LCL Shipping] Order Quote Success';

  constructor(public shipmentId: number) {}
}

export class CreateQuote {
  public static readonly type = '[LCL Shipping] Create Quote';

  constructor(public payload: { companyId: number; quote: QuoteUpdateModel; shipper?: EntityModel; consignee?: EntityModel }) {}
}

export class CreateQuoteSuccess {
  public static readonly type = '[LCL Shipping] Create Quote Success';

  constructor(public quoteId: number) {}
}

export class CreateManualQuote {
  public static readonly type = '[LCL Shipping] Create Manual Quote';

  constructor(public payload: { companyId: number; quote: QuoteManualModel }) {}
}

export class CreateManualQuoteSuccess {
  public static readonly type = '[LCL Shipping] Create Manual Quote Success';

  constructor() {}
}

export class CreateExternalManualQuote {
  public static readonly type = '[LCL Shipping] Create External Manual Quote';

  constructor(public payload: { quote: QuoteManualModel; recaptchaToken: string }) {}
}

export class CreateExternalManualQuoteSuccess {
  public static readonly type = '[LCL Shipping] Create External Manual Quote Success';

  constructor() {}
}

export class CreateExternalManualQuoteError {
  public static readonly type = '[LCL Shipping] Create External Manual Quote Error';

  constructor(public payload: { tag: string }) {}
}

export class CreateExistingUserManualQuote {
  public static readonly type = '[LCL Shipping] Create Existing User Manual Quote';

  constructor(public payload: { quote: QuoteManualModel; email: string; password: string; formGroup: UntypedFormGroup }) {}
}

export class CreateExistingUserManualQuoteError {
  public static readonly type = '[LCL Shipping] Create Existing User Manual Quote Error';

  constructor(public payload: { tag: string }) {}
}

export class UpdateQuote {
  public static readonly type = '[LCL Shipping] Update Quote';

  constructor(public payload: { quote: QuoteUpdateModel; isLoading?: boolean }) {}
}

export class UpdateQuoteIncoterms {
  public static readonly type = '[LCL Shipping] Update Quote Incoterms';

  constructor(public payload: QuoteUpdateModel) {}
}

export class UpdateQuoteDraft {
  public static readonly type = '[LCL Shipping] Update Quote Draft';

  constructor(public payload: QuoteModel) {}
}

export class UpdateQuoteVoyage {
  public static readonly type = '[LCL Shipping] Update Quote Voyage';

  constructor(public payload: { quoteId: number; voyageId: number }) {}
}

export class UpdateQuoteSuccess {
  public static readonly type = '[LCL Shipping] Update Quote Success';

  constructor(public quoteId: number) {}
}

export class FetchLCLPendingBills {
  public static readonly type = '[LCL Shipping] Fetch LCL Pending Bills';

  constructor(public payload: { companyId: number; referenceNumber: string; coupon?: string }) {}
}

export class ResetLCLPendingBills {
  public static readonly type = '[LCL Shipping] Reset LCL Pending Bills';
}

export class UpdateShipment {
  public static readonly type = '[LCL Shipping] Update Shipment';

  constructor(public payload: LCLShipmentModel) {}
}

export class UpdateShipmentSuccess {
  public static readonly type = '[LCL Shipping] Update Shipment Success';
}

export class PatchShipmentEntity {
  public static readonly type = '[LCL Shipping] Patch Shipment Entity';

  constructor(public payload: { entity: EntityModel; fieldKey: string }) {}
}

export class UpdateLoadingState {
  public static readonly type = '[LCL Shipping] Update Loading State';

  constructor(public payload: boolean) {}
}

export class CreateLCLLinkToISF {
  public static readonly type = '[LCL Shipping] Create Shipment ISF';

  constructor(public payload: { shipment: LCLShipmentModel; data: LclIsfEsLinkModel }) {}
}

export class CreateLCLLinkToISFSuccess {
  public static readonly type = '[LCL Shipping] Create Shipment ISF Success';
  constructor(public referenceNumber?: string) {}
}

export class CreateLCLLinkToES {
  public static readonly type = '[LCL Shipping] Create Shipment ES';

  constructor(
    public payload: { shipment: LCLShipmentModel; freightCost: number; data: LclIsfEsLinkModel; invoiceDocument?: DocumentModel },
  ) {}
}

export class CreateLCLLinkToESSuccess {
  public static readonly type = '[LCL Shipping] Create Shipment ES Success';
  constructor(public referenceNumber?: string) {}
}

export class GetLCLMarineInsurance {
  public static readonly type = '[LCL Shipping] Get Marine Insurance';
  constructor(public payload: { freightShipment?: LCLShipmentModel; freightQuote?: QuoteModel }) {}
}

export class RemoveLCLMarineInsurance {
  public static readonly type = '[LCL Shipping] Remove Marine Insurance';
  constructor(public shipmentId: number) {}
}

export class OpenLCLMarineInsuranceQuoteSidebar {
  public static readonly type = '[LCL Shipping] Open Marine Insurance Quote Sidebar';
  constructor(public payload: { booking?: QuoteModel; shipment?: LCLShipmentModel }) {}
}

export class CloseLCLMarineInsuranceQuoteSidebar {
  public static readonly type = '[LCL Shipping] Close Marine Insurance Quote Sidebar';
  constructor(public isDismiss: boolean) {}
}

export class FetchLCLMarineInsurancePendingBills {
  public static readonly type = '[LCL Shipping] Fetch LCL Related Marine Insurance Pending Bills';

  constructor(public payload: { companyId: number; referenceNumber: string }) {}
}

export class FetchLCLMarineInsuranceDocuments {
  public static readonly type = '[LCL Shipping] Fetch LCL Related Marine Insurance Documents';

  constructor(public shipmentId: number) {}
}

export class SubmitExitIntent {
  public static readonly type = '[LCL Shipping] Submit Exit Intent';

  constructor(public payload: SurveyInfoModel) {}
}

export class SubmitExitIntentSuccess {
  public static readonly type = '[LCL Shipping] Submit Exit Intent Success';

  constructor() {}
}

export class SubmitExitIntentError {
  public static readonly type = '[LCL Shipping] Submit Exit Intent Error';

  constructor() {}
}
